import React, { Component, createRef, RefObject } from 'react'
import { debounce, DebouncedFunc } from 'lodash'
import { injectIntl } from 'react-intl'
import Dehaze from '@material-ui/icons/Dehaze'
import isEqual from 'lodash/isEqual'
import {
  Checkbox,
  Radio,
  ListItemText,
  FormControl,
  MenuItem,
  Select,
  Typography,
  TextField,
  Button,
  FormHelperText,
} from '@material-ui/core'
import isEmpty from 'lodash/isEmpty'
import { connect } from 'react-redux'
import {
  addDynamicOption,
  fetchJobTaskList,
  fetchLookupTableSourceData,
} from 'reducers/myJobs/reducer'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import withPromise from 'helpers/withPromise'
import {
  StyledClearSelectMenuItem,
  StyledClearSelectButton,
} from 'routes/App/Apps/MyJobs/JobFields/JobFields.styles'
import { ReactComponent as CheckIcon } from 'assets/img/check-icon.svg'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import { Flex } from 'components/common/styles'
import { isParentLookupInputsFilledIn } from 'helpers/myJobs'
import {
  StyledHorizontalScrollView,
  StyledListView,
  StyledSelectInputOption,
} from '../styles'
import {
  JobSelectInputProps,
  JobSelectInputState,
  Options,
  SelectInputValue,
  Site,
} from './JobFields.types'
import JobImagesInput from './JobImagesInput'
import { equalMultiUnitValues } from './jobFieldsHelper'
import messages from '../translate'

function normalizeMultipleSelectValue(
  value: SelectInputValue,
  parameters?: JobSelectInputProps['inputInfo']['parameters'],
): SelectInputValue {
  if (!parameters?.multiple || !Array.isArray(value)) {
    return value
  }

  return value.map(item => String(item))
}

type inputViewPropsTypes = {
  isInputDisabled: boolean
  multiple?: boolean
  valueRenderMethod?: (value?: unknown) => React.ReactNode
  optionsSource?: string
}

export class JobSelectInput extends Component<
  JobSelectInputProps,
  JobSelectInputState
> {
  public controlRef: RefObject<HTMLDivElement>

  public searchInputRef: RefObject<HTMLInputElement>

  public horizontalScrollViewRef: RefObject<HTMLInputElement>

  public debouncedInputBlur: DebouncedFunc<() => void>

  constructor(props: JobSelectInputProps) {
    super(props)
    const { parameters, options } = props.inputInfo
    const defaultValue = parameters && 'multiple' in parameters ? [] : ''

    this.state = {
      value: defaultValue,
      oldValue: defaultValue,
      evtTaskInputId: '',
      anchor: null,
      filteredOptions: options,
      searchQuery: '',
      errorMessage: '',
      isUpdating: false,
    }

    this.debouncedInputBlur = debounce(this.onInputBlur.bind(this), 1000)
    this.controlRef = createRef()
    this.searchInputRef = createRef()
    this.horizontalScrollViewRef = createRef()
  }

  componentDidMount() {
    const { inputData, inputInfo } = this.props
    const { parameters } = inputInfo
    const defaultValue = parameters && 'multiple' in parameters ? [] : ''
    const { value = defaultValue, evtInputId } = inputData

    this.setInputValue(value, evtInputId, parameters)
  }

  shouldComponentUpdate(nextProps: JobSelectInputProps) {
    const { inputInfo, inputData } = this.props
    const { inputInfo: nextInputInfo, inputData: nextInputData } = nextProps
    const { parameters: nextParameters } = nextInputInfo
    const defaultValue =
      nextParameters && 'multiple' in nextParameters ? [] : ''

    if (
      !isEqual(inputInfo, nextInputInfo) ||
      !isEqual(inputData, nextInputData)
    ) {
      const { evtInputId, value = defaultValue } = nextInputData

      this.setInputValue(value, evtInputId, nextParameters)

      return false
    }

    return true
  }

  componentDidUpdate(
    prevProps: JobSelectInputProps,
    prevState: JobSelectInputState,
  ) {
    const { searchQuery: prevSearchQuery } = prevState
    const { searchQuery: newSearchQuery, value, oldValue } = this.state
    const {
      dependency: {
        currentEvtTask: { id: currentEvtTaskId },
      },
      site: { name: siteName },
      inputInfo: { parameters },
    } = this.props

    if (prevSearchQuery !== newSearchQuery && this.searchInputRef.current) {
      this.searchInputRef.current.focus()
    }

    if (
      currentEvtTaskId &&
      isEqual(value, oldValue) &&
      parameters?.lookup_table_source?.equal_to_site_name &&
      (!value || isEmpty(value))
    ) {
      this.setState(
        {
          value: parameters.multiple ? [siteName] : siteName,
        },
        this.onInputBlur,
      )
    }
  }

  setInputValue = (
    value: SelectInputValue,
    evtTaskInputId: number,
    parameters?: JobSelectInputProps['inputInfo']['parameters'],
  ) => {
    const params = parameters ?? this.props.inputInfo.parameters
    const normalized = normalizeMultipleSelectValue(value, params)

    this.setState({
      value: normalized,
      oldValue: normalized,
      evtTaskInputId,
    })
  }

  setResultId = ({ id }: { id: number }): void => {
    this.setState({ evtTaskInputId: id })
  }

  onListChange = ({
    target,
  }: React.ChangeEvent<{ name?: string; value: unknown }>): void => {
    const {
      inputInfo: { parameters },
    } = this.props
    let value = target.value as SelectInputValue

    value = normalizeMultipleSelectValue(value, parameters)

    this.setState({ value })
  }

  handleJobTaskAutoSaveError = ({
    errors: { result: errorMessage },
  }: {
    errors: { result: string }
  }): void => {
    this.setState({ errorMessage })
  }

  intersection = (arrBase: Array<string>, arr: Array<string>): Array<string> =>
    arrBase.filter(value => arr.includes(value.toString()))

  getListLookupTableSourceData = (
    inputId: number,
    evtTaskId: number,
    taskId: number,
    evtInputId: number,
  ): void => {
    const { fetchLookupTableSourceData } = this.props

    withPromise(fetchLookupTableSourceData, {
      inputId,
      evtTask: evtTaskId,
      taskId,
      evtInputId,
    }).then(({ error, options }) => {
      if (!error) {
        this.setState({ filteredOptions: options })
      }
    })
  }

  onInputBlur = (): void => {
    const {
      inputInfo: { id: inputId },
      inputs,
      activeJobId,
      inputData: { evtInputId },
      onJobTaskAutoSave,
      dependency: { currentEvtTask, evtTaskMatches },
      handleDependentLookupInputs,
      setIsDebouncing,
    } = this.props
    const { value, oldValue, evtTaskInputId } = this.state

    if (!isEqual(value, oldValue)) {
      const nonMultipleValue = value === null ? null : String(value)

      this.setState({
        errorMessage: '',
        isUpdating: true,
      })

      onJobTaskAutoSave(
        {
          evtInputId: evtInputId || evtTaskInputId,
          result: Array.isArray(value) ? value.map(String) : nonMultipleValue,
          input: inputId,
          setResultId: this.setResultId,
        },
        () => {
          handleDependentLookupInputs(
            inputs,
            evtTaskMatches || [currentEvtTask],
            inputId,
            activeJobId,
          )
          this.setState({ isUpdating: false })
          setIsDebouncing(false)
        },
        this.handleJobTaskAutoSaveError,
      )

      this.setState({ oldValue: value })
    }
  }

  renderValues = (): string => {
    const { options } = this.props
    const { value } = this.state
    let result = ''

    if (Array.isArray(value) && value.length > 0) {
      result = value
        .map(item => this.getCheckedTranslatableMessage(options[item] || item))
        .join(', ')
    }

    return result
  }

  handleControlClick = (): void => {
    const {
      inputInfo: { id: inputId, parameters },
      inputData: { evtInputId },
      dependency: { currentEvtTask, currentTaskID },
    } = this.props
    const { anchor } = this.state

    if (!anchor) {
      if (
        parameters &&
        'lookup_table_source' in parameters &&
        currentEvtTask.id
      ) {
        const { lookup_table_source: { parents } = {} } = parameters
        const suitableEvtInputId = Array.isArray(evtInputId)
          ? evtInputId[0]
          : evtInputId

        if (
          !parents?.length ||
          isParentLookupInputsFilledIn(parents, currentEvtTask.evt_inputs)
        ) {
          this.getListLookupTableSourceData(
            inputId,
            currentEvtTask.id,
            currentTaskID,
            suitableEvtInputId,
          )
        }
      }

      this.setState({
        anchor: this.controlRef.current,
      })
    }
  }

  getCheckedTranslatableMessage = (
    message: string | number,
  ): string | number => {
    const {
      intl: { formatMessage },
    } = this.props
    const isTranslatableMessage =
      typeof message === 'string' && message.includes('_')

    return isTranslatableMessage ? formatMessage({ id: message }) : message
  }

  onChangeSearchField = ({
    target: { value: targetValue },
  }: {
    target: { value: string }
  }): void => {
    const { options } = this.props

    const asArray = Object.entries(options)
    const filtered = asArray.filter(
      ([, value]) =>
        value.toLowerCase().indexOf(targetValue.toLowerCase().trim()) >= 0,
    )
    const filteredOptions = Object.fromEntries(filtered)

    this.setState({
      filteredOptions,
      searchQuery: targetValue,
    })
  }

  onAddOption = (value: string): void => {
    const { addDynamicOption, index: id, fetchJobTaskList } = this.props
    const payload = {
      id,
      data: {
        option: value,
      },
    }

    withPromise(addDynamicOption, payload).then((response: Options) => {
      if (!response.error) {
        const {
          inputInfo: { id: input, parameters },
          inputData: { evtInputId },
          onJobTaskAutoSave,
          options,
          activeJobId,
        } = this.props
        const { evtTaskInputId, oldValue } = this.state
        const multiple = parameters && parameters.multiple
        const [resultKey] = Object.keys(response)
        const result = multiple ? [...(oldValue as []), resultKey] : resultKey

        withPromise(fetchJobTaskList, {
          activeJobId,
          skipLoader: true,
        }).then(() => {
          this.setState({ isUpdating: true })

          onJobTaskAutoSave(
            {
              evtInputId: evtInputId || evtTaskInputId,
              result,
              input,
              setResultId: this.setResultId,
            },
            () => {
              this.setState({ isUpdating: false })
            },
          )

          this.setState({
            oldValue: result,
            value: result,
            filteredOptions: {
              ...options,
              ...response,
            },
          })
        })
      }
    })
  }

  isSelected = (itemValue: string | number): boolean => {
    const { value } = this.state
    const key = String(itemValue)

    return (
      Array.isArray(value) && value.some(selected => String(selected) === key)
    )
  }

  handleListViewOptionClick = (item: string, multiple?: boolean) => {
    const { value: currentValue } = this.state
    const { setIsDebouncing } = this.props
    let newValue: string | number | Array<string | number>

    if (
      (multiple &&
        this.isSelected(item) &&
        Array.isArray(currentValue) &&
        currentValue.length === 1) ||
      item === currentValue
    ) {
      this.handleClearSelect()

      return
    }

    if (multiple && Array.isArray(currentValue)) {
      newValue = this.isSelected(item)
        ? currentValue.filter(value => value !== String(item))
        : [...currentValue, String(item)]
    } else {
      newValue = String(item)
    }

    this.setState({ value: newValue }, () => {
      setIsDebouncing(true)
      this.debouncedInputBlur()
    })
  }

  isSelectAllEnabled = (multiple?: boolean): boolean => {
    const {
      inputInfo: { parameters },
    } = this.props

    return !!multiple && !!parameters?.select_all
  }

  isAllOptionsSelected = (choices: Array<string>): boolean => {
    const { value } = this.state

    if (!choices.length || !Array.isArray(value)) {
      return false
    }

    return choices.every(choice =>
      value.some(selected => String(selected) === String(choice)),
    )
  }

  handleSelectAllClick = (
    choices: Array<string>,
    opts?: { deferSave?: boolean },
  ): void => {
    const { setIsDebouncing } = this.props

    if (!choices.length) {
      return
    }

    const newValue = this.isAllOptionsSelected(choices)
      ? []
      : choices.map(String)
    const deferSave = !!opts?.deferSave

    this.setState({ value: newValue }, () => {
      if (!deferSave) {
        setIsDebouncing(true)
        this.debouncedInputBlur()
      }
    })
  }

  isListViewOptionSelected = (
    item: string | number,
    multiple?: boolean,
  ): boolean => {
    const { value } = this.state

    return multiple ? this.isSelected(item) : value === String(item)
  }

  renderListView = ({ multiple, isInputDisabled }: inputViewPropsTypes) => {
    const { filteredOptions } = this.state
    const {
      inputInfo: { parameters },
    } = this.props
    const { intersection } = this
    const orderedChoices = parameters?.ordered_choices
      ? intersection(parameters.ordered_choices, Object.keys(filteredOptions))
      : Object.keys(filteredOptions)
    const optionsValues = Object.values(filteredOptions)
    const lengthOfSymbols = optionsValues.reduce(
      (result, optionValue) => result + optionValue.length,
      0,
    )
    const isMatrixView =
      (optionsValues.length === 3 && lengthOfSymbols <= 14) ||
      (optionsValues.length === 2 && lengthOfSymbols <= 20)

    return (
      <StyledListView isMatrixView={isMatrixView} disabled={isInputDisabled}>
        {this.isSelectAllEnabled(multiple) && (
          <StyledSelectInputOption
            key="select-all"
            checked={this.isAllOptionsSelected(orderedChoices)}
            width={!isMatrixView ? '100%' : undefined}
            nowrap={isMatrixView}
            onClick={() => {
              this.handleSelectAllClick(orderedChoices)
            }}>
            <Checkbox
              color="primary"
              checked={this.isAllOptionsSelected(orderedChoices)}
            />
            {this.props.intl.formatMessage({ id: 'select_all' })}
          </StyledSelectInputOption>
        )}
        {orderedChoices.map(item => (
          <StyledSelectInputOption
            key={String(item)}
            checked={this.isListViewOptionSelected(item, multiple)}
            width={!isMatrixView ? '100%' : undefined}
            nowrap={isMatrixView}
            onClick={() => {
              this.handleListViewOptionClick(item, multiple)
            }}>
            {multiple ? (
              <Checkbox
                color="primary"
                checked={this.isListViewOptionSelected(item, multiple)}
              />
            ) : (
              <Radio
                color="primary"
                checked={this.isListViewOptionSelected(item, multiple)}
              />
            )}
            {this.getCheckedTranslatableMessage(filteredOptions[item])}
          </StyledSelectInputOption>
        ))}
      </StyledListView>
    )
  }

  renderHorizontalScrollView = ({
    multiple,
    isInputDisabled,
  }: inputViewPropsTypes) => {
    const horizontalScrollItemWidth = this.horizontalScrollViewRef.current
      ? this.horizontalScrollViewRef.current.offsetWidth - 50
      : 320
    const { filteredOptions } = this.state
    const {
      inputInfo: { parameters },
    } = this.props
    const { intersection } = this
    const orderedChoices = parameters?.ordered_choices
      ? intersection(parameters.ordered_choices, Object.keys(filteredOptions))
      : Object.keys(filteredOptions)

    return (
      <StyledHorizontalScrollView
        ref={this.horizontalScrollViewRef}
        disabled={isInputDisabled}>
        {this.isSelectAllEnabled(multiple) && (
          <StyledSelectInputOption
            key="select-all"
            checked={this.isAllOptionsSelected(orderedChoices)}
            width={`${horizontalScrollItemWidth}px`}
            onClick={() => {
              this.handleSelectAllClick(orderedChoices)
            }}>
            <Checkbox
              color="primary"
              checked={this.isAllOptionsSelected(orderedChoices)}
            />
            {this.props.intl.formatMessage({ id: 'select_all' })}
          </StyledSelectInputOption>
        )}
        {orderedChoices.map(item => (
          <StyledSelectInputOption
            key={String(item)}
            checked={this.isListViewOptionSelected(item, multiple)}
            width={`${horizontalScrollItemWidth}px`}
            onClick={() => {
              this.handleListViewOptionClick(item, multiple)
            }}>
            {multiple ? (
              <Checkbox
                color="primary"
                checked={this.isListViewOptionSelected(item, multiple)}
              />
            ) : (
              <Radio
                color="primary"
                checked={this.isListViewOptionSelected(item, multiple)}
              />
            )}
            {this.getCheckedTranslatableMessage(filteredOptions[item])}
          </StyledSelectInputOption>
        ))}
      </StyledHorizontalScrollView>
    )
  }

  handleClearSelect = () => {
    const { value } = this.state
    const {
      inputInfo: { parameters },
      site: { name: siteName },
    } = this.props
    const multiple = parameters && parameters.multiple
    const equalsToSiteName = parameters?.lookup_table_source?.equal_to_site_name
    const multipleDefaultValue = equalsToSiteName ? [siteName] : []

    this.setState(
      {
        oldValue: value,
        value: multiple ? multipleDefaultValue : null,
      },
      this.onInputBlur,
    )
  }

  renderDropdownView = ({
    multiple,
    isInputDisabled,
    valueRenderMethod,
    optionsSource,
  }: inputViewPropsTypes) => {
    const {
      inputInfo: { title, parameters },
      options,
      site: { name: siteName },
      intl: { formatMessage },
      inputHint,
    } = this.props
    const {
      value,
      anchor,
      filteredOptions,
      searchQuery,
      isUpdating,
    } = this.state
    const dynamicOptionsAllowed = parameters && parameters.dynamic_options
    const canAddOption =
      searchQuery &&
      !Object.values(filteredOptions).find(
        optionValue => optionValue === searchQuery,
      )
    const equalsToSiteName = parameters?.lookup_table_source?.equal_to_site_name
    const hasClearOption = Array.isArray(value) ? !!value.length : !!value
    const dropdownMenuProps = {
      classes: {
        list: 'limited-menu-list',
      } as { list: 'limited-menu-list' },
      anchorEl: anchor,
      getContentAnchorEl: null,
      anchorOrigin: {
        horizontal: 'left' as const,
        vertical: -2,
      },
    }
    const { intersection } = this
    const orderedChoices = parameters?.ordered_choices?.length
      ? intersection(parameters.ordered_choices, Object.keys(filteredOptions))
      : Object.keys(filteredOptions)

    return (
      <>
        <div
          role="presentation"
          className="job-field-wrapper"
          ref={this.controlRef}>
          <Dehaze className="job-field-icon" />
          <Select
            multiple={multiple}
            className="job-field-input-box"
            disabled={isInputDisabled}
            value={value}
            MenuProps={dropdownMenuProps}
            onChange={this.onListChange}
            renderValue={valueRenderMethod}
            onOpen={() => {
              if (!isInputDisabled) {
                this.handleControlClick()
              }
            }}
            onClose={() => {
              this.setState(
                {
                  searchQuery: '',
                  filteredOptions: {
                    ...options,
                  },
                  anchor: null,
                },
                this.onInputBlur,
              )
            }}
            inputProps={{
              className: 'job-field-input',
            }}>
            <MenuItem
              disabled
              className="job-label-default"
              style={{
                maxWidth: anchor ? anchor.offsetWidth : undefined,
              }}>
              <Typography variant="caption">{title}</Typography>
            </MenuItem>
            <MenuItem
              className="search-field"
              onKeyDown={e => {
                e.stopPropagation()
              }}
              button={false}>
              <TextField
                placeholder={formatMessage({ id: 'search' })}
                value={searchQuery}
                inputProps={{
                  ref: this.searchInputRef,
                }}
                onClick={e => {
                  e.stopPropagation()
                }}
                onChange={this.onChangeSearchField}
              />
            </MenuItem>
            {hasClearOption && (
              <StyledClearSelectMenuItem
                disabled={equalsToSiteName && isEqual(value, [siteName])}
                onKeyDown={e => {
                  e.stopPropagation()
                }}>
                <StyledClearSelectButton
                  role="presentation"
                  onClick={e => {
                    e.stopPropagation()

                    if (!isUpdating) {
                      this.handleClearSelect()
                    }
                  }}>
                  {formatMessage(messages.clearSelection)}
                </StyledClearSelectButton>
              </StyledClearSelectMenuItem>
            )}
            {this.isSelectAllEnabled(multiple) && (
              <MenuItem
                button={false}
                onKeyDown={e => {
                  e.stopPropagation()
                }}
                onClick={e => {
                  e.stopPropagation()
                }}>
                <div
                  role="presentation"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    cursor: 'pointer',
                  }}
                  onClick={e => {
                    e.stopPropagation()
                    this.handleSelectAllClick(orderedChoices, {
                      deferSave: true,
                    })
                  }}>
                  <Checkbox
                    color="primary"
                    checked={this.isAllOptionsSelected(orderedChoices)}
                  />
                  <ListItemText primary={formatMessage({ id: 'select_all' })} />
                </div>
              </MenuItem>
            )}
            {orderedChoices.map(item => {
              const optionKey = String(item)

              return (
                <MenuItem
                  button
                  disabled={optionKey === String(siteName) && equalsToSiteName}
                  key={optionKey}
                  value={optionKey}>
                  {multiple && (
                    <Checkbox
                      color="primary"
                      checked={this.isSelected(optionKey)}
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                  <ListItemText
                    primary={this.getCheckedTranslatableMessage(
                      filteredOptions[item],
                    )}
                  />
                </MenuItem>
              )
            })}
            {isEmpty(filteredOptions) && (
              <MenuItem disabled className="no-results-message">
                {formatMessage({ id: 'no_results' })}
              </MenuItem>
            )}
            {!!canAddOption && !optionsSource && !!dynamicOptionsAllowed && (
              <MenuItem
                button={false}
                style={{
                  maxWidth: anchor ? anchor.offsetWidth : undefined,
                }}>
                <Button
                  className="add-option-button"
                  onClick={e => {
                    e.stopPropagation()
                    this.onAddOption(searchQuery)
                  }}>
                  <span className="label">
                    + {formatMessage({ id: 'add' })}
                  </span>{' '}
                  {searchQuery}
                </Button>
              </MenuItem>
            )}
          </Select>
        </div>
        <div>{inputHint}</div>
      </>
    )
  }

  renderSelectView = (inputViewProps: inputViewPropsTypes) => {
    const {
      inputInfo: { parameters: { display_options } = {} },
    } = this.props
    const { filteredOptions } = this.state
    const horizontalScrollCondition =
      display_options === 'horizontal_scroll' &&
      ifWindowWidthLessThan(TABLET_BREAKPOINT) &&
      Object.values(filteredOptions).length > 5
    const listCondition =
      display_options === 'list' ||
      (display_options === 'horizontal_scroll' &&
        (!ifWindowWidthLessThan(TABLET_BREAKPOINT) ||
          Object.values(filteredOptions).length <= 5))

    if (horizontalScrollCondition) {
      return this.renderHorizontalScrollView(inputViewProps)
    }

    if (listCondition) {
      return this.renderListView(inputViewProps)
    }

    return this.renderDropdownView(inputViewProps)
  }

  render() {
    const {
      disabled,
      className,
      inputInfo: { title, parameters, required },
      dependency: { currentEvtTask },
      options,
      multiChoiceMode,
      intl: { formatMessage },
      inputData: { valueIsEqual },
    } = this.props
    const { value, errorMessage, isUpdating, filteredOptions } = this.state
    const multiple = parameters && parameters.multiple
    const optionsSource = parameters && parameters.options_source
    const lookupTableSource = parameters && 'lookup_table_source' in parameters
    const equalValuesPlaceholder = equalMultiUnitValues(
      multiChoiceMode,
      valueIsEqual,
    )
    const drawAsterisk = required ? '*' : ''
    const singleSelectIndex =
      value !== null && !Array.isArray(value) ? value : ''
    const index = Array.isArray(value) ? value[0] : singleSelectIndex
    const valueRenderMethod = () =>
      multiple
        ? this.renderValues()
        : this.getCheckedTranslatableMessage(options[index] || index)
    const isDisabledLookupSelect =
      lookupTableSource &&
      ((parameters?.lookup_table_source?.parents?.length &&
        currentEvtTask.evt_inputs &&
        !isParentLookupInputsFilledIn(
          parameters.lookup_table_source?.parents,
          currentEvtTask.evt_inputs,
        )) ||
        (!parameters?.multiple &&
          parameters?.lookup_table_source?.equal_to_site_name))
    const isInputDisabled =
      disabled || isUpdating || !currentEvtTask.id || !!isDisabledLookupSelect
    const horizontalScrollCondition =
      parameters?.display_options === 'horizontal_scroll' &&
      ifWindowWidthLessThan(TABLET_BREAKPOINT) &&
      Object.values(filteredOptions).length > 5 &&
      (typeof value === 'number' || (value !== null && !!value.length))

    return (
      <div>
        {optionsSource === 'images' ? (
          <JobImagesInput {...this.props} />
        ) : (
          <FormControl
            fullWidth
            className={className}
            disabled={isInputDisabled}>
            <Flex justify="space-between">
              <span className="job-field-label">
                {title} {drawAsterisk}
              </span>
              {horizontalScrollCondition && (
                <span>
                  <CheckIcon /> {formatMessage(messages.chosen)}
                </span>
              )}
            </Flex>
            {this.renderSelectView({
              multiple,
              isInputDisabled,
              valueRenderMethod,
              optionsSource,
            })}
            {!!errorMessage && (
              <FormHelperText className="error-message">
                {errorMessage}
              </FormHelperText>
            )}
            {!equalValuesPlaceholder && (
              <FormHelperText className="warning-message">
                {formatMessage(messages.valuesAreDifferent)}
              </FormHelperText>
            )}
          </FormControl>
        )}
      </div>
    )
  }
}

export default connect(
  (state: { getIn: (path: Array<string>) => Site }) => ({
    site: state.getIn(['profile', 'profile', 'site']),
  }),
  {
    addDynamicOption,
    fetchJobTaskList,
    fetchLookupTableSourceData,
  },
)(injectIntl(JobSelectInput))
